// Records what every Firestore read actually costs, so a slow page can be
// diagnosed with numbers instead of guesses.
//
// Each `.get()` is already wrapped for the loading indicator (see
// firebase/firebase.utils.js); this hangs timing and document counts off the
// same wrapper. Off by default — turn it on from the browser console:
//
//   algProfiler.start()      // begin recording, clears anything previous
//   location.reload()        // or navigate to the page you want to profile
//   algProfiler.report()     // console table, slowest first, plus totals
//   algProfiler.stop()
//
// It is deliberately opt-in rather than always-on: recording every read costs
// nothing measurable, but printing to the console on a busy page does.

const MAX_ENTRIES = 500;

let enabled = false;
let entries = [];

export const isProfiling = () => enabled;

export const startProfiling = () => {
  entries = [];
  enabled = true;
  console.info(
    "[algProfiler] recording Firestore reads — run algProfiler.report() when the page has settled",
  );
};

export const stopProfiling = () => {
  enabled = false;
  console.info("[algProfiler] stopped");
};

export const recordQuery = (entry) => {
  if (!enabled) return;
  // A runaway page must not grow this without bound.
  if (entries.length >= MAX_ENTRIES) return;
  entries.push(entry);
};

export const getQueryLog = () => entries.slice();

/**
 * Describes a Firestore reference for the log. The internals differ per
 * reference type and are private API, so every step is defensive — a label is
 * never worth throwing for.
 */
export const describeRef = (ref) => {
  try {
    // DocumentReference and CollectionReference both expose `path`.
    if (typeof ref.path === "string") return ref.path;

    const internal = ref._query || ref._delegate;
    if (!internal) return "query";

    const path =
      internal.path && internal.path.canonicalString
        ? internal.path.canonicalString()
        : "query";

    const filters = (internal.filters || [])
      .map((filter) => {
        const field =
          filter.field && filter.field.canonicalString
            ? filter.field.canonicalString()
            : "?";
        const op = filter.op ? filter.op.name || String(filter.op) : "?";
        return `${field} ${op}`;
      })
      .join(", ");

    const limit = internal.limit ? ` limit ${internal.limit}` : "";
    return filters ? `${path} [${filters}]${limit}` : `${path}${limit}`;
  } catch (error) {
    return "query";
  }
};

// Firestore returns a QuerySnapshot (has `.size`) or a DocumentSnapshot
// (has `.exists`); count both as documents read.
const countDocs = (snapshot) => {
  if (!snapshot) return 0;
  if (typeof snapshot.size === "number") return snapshot.size;
  if (typeof snapshot.exists === "boolean") return snapshot.exists ? 1 : 0;
  return 0;
};

export const timeQuery = (ref, run) => {
  if (!enabled) return run();
  const startedAt = Date.now();
  return run().then(
    (snapshot) => {
      recordQuery({
        label: describeRef(ref),
        ms: Date.now() - startedAt,
        docs: countDocs(snapshot),
        failed: false,
      });
      return snapshot;
    },
    (error) => {
      recordQuery({
        label: describeRef(ref),
        ms: Date.now() - startedAt,
        docs: 0,
        failed: true,
      });
      throw error;
    },
  );
};

export const reportProfile = () => {
  if (!entries.length) {
    console.info(
      "[algProfiler] nothing recorded — call algProfiler.start() first, then load the page",
    );
    return [];
  }

  // Group by label: the chunked fan-outs issue the same query shape many
  // times, and the total across them is the number that matters.
  const byLabel = new Map();
  entries.forEach((entry) => {
    const row = byLabel.get(entry.label) || {
      query: entry.label,
      calls: 0,
      docs: 0,
      totalMs: 0,
      slowestMs: 0,
    };
    row.calls += 1;
    row.docs += entry.docs;
    row.totalMs += entry.ms;
    row.slowestMs = Math.max(row.slowestMs, entry.ms);
    byLabel.set(entry.label, row);
  });

  const rows = [...byLabel.values()].sort((a, b) => b.totalMs - a.totalMs);
  console.table(rows);
  console.info(
    `[algProfiler] ${entries.length} reads, ${rows.reduce((a, r) => a + r.docs, 0)} documents. ` +
      "Reads overlap, so summing the times overstates wall clock — the top rows are where to look.",
  );
  return rows;
};

if (typeof window !== "undefined") {
  window.algProfiler = {
    start: startProfiling,
    stop: stopProfiling,
    report: reportProfile,
    log: getQueryLog,
  };
}
